/**
 * RichTextField — TipTap ベースの WYSIWYG リッチテキスト入力。
 *
 * - 入力欄自体に `prose` クラスを適用し、見出し・太字・リスト等が
 *   ライブで反映される（公開資料・印刷 PDF と同じスタイル）。
 * - 値は HTML 文字列で扱い、既存 SimpleConfig/QuoteConfig/CalloutConfig の
 *   `html` フィールドにそのまま入る。
 * - blur で onCommit。値の変化は onChange に都度通知。
 */
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold as BoldIcon,
  Heading2,
  Heading3,
  Italic as ItalicIcon,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Label } from '@/components/ui/input';

interface Props {
  label: string;
  /** 現在の HTML 文字列 */
  value: string;
  /** 入力毎に呼ばれる（debounced auto-save 想定） */
  onChange: (html: string) => void;
  /** blur 時に呼ばれる */
  onCommit?: () => void;
  placeholder?: string;
  /** 高さの目安 (px)。指定なしで最小 200px */
  minHeight?: number;
}

export function RichTextField({
  label,
  value,
  onChange,
  onCommit,
  placeholder,
  minHeight = 200,
}: Props) {
  // 外部からの value 更新を反映するための ref
  const lastValueRef = useRef(value);
  // IME (日本語入力) 中フラグ。composition 中は外部からの setContent を
  // スキップしてカーソル末尾飛びを防ぐ。
  const composingRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Placeholder.configure({
        placeholder: placeholder ?? '本文を入力...',
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class:
          'prose prose-sm dark:prose-invert max-w-none focus:outline-none px-3 py-2 leading-relaxed',
        style: `min-height: ${minHeight}px`,
      },
      handleDOMEvents: {
        compositionstart: () => {
          composingRef.current = true;
          return false;
        },
        compositionend: () => {
          composingRef.current = false;
          return false;
        },
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastValueRef.current = html;
      onChange(html);
    },
    onBlur: () => {
      if (composingRef.current) return;
      onCommit?.();
    },
  });

  // 外部 value 変更 (セクション切替など) を取り込む。
  // 同一値なら no-op。IME 中もスキップ。
  useEffect(() => {
    if (!editor) return;
    if (composingRef.current) return;
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor]);

  return (
    <div className="space-y-1">
      <Label className="text-[11px]">{label}</Label>
      <div className="border border-[var(--border)] rounded-md bg-[var(--bg-input)] overflow-hidden">
        {editor && <Toolbar editor={editor} />}
        <div className="border-t border-[var(--border)]">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const btn = (active: boolean) =>
    `px-1.5 py-1 rounded-sm text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors ${
      active ? 'bg-[var(--accent-subtle)] text-[var(--accent)]' : ''
    }`;

  const setLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL を入力:', previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="flex items-center gap-0.5 px-1.5 py-1 bg-[var(--bg-elevated)] flex-wrap">
      <button
        type="button"
        title="見出し H2"
        className={btn(editor.isActive('heading', { level: 2 }))}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="size-3.5" />
      </button>
      <button
        type="button"
        title="見出し H3"
        className={btn(editor.isActive('heading', { level: 3 }))}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="size-3.5" />
      </button>
      <span className="w-px h-4 bg-[var(--border)] mx-1" />
      <button
        type="button"
        title="太字 (Cmd+B)"
        className={btn(editor.isActive('bold'))}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon className="size-3.5" />
      </button>
      <button
        type="button"
        title="斜体 (Cmd+I)"
        className={btn(editor.isActive('italic'))}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon className="size-3.5" />
      </button>
      <button
        type="button"
        title="下線 (Cmd+U)"
        className={btn(editor.isActive('underline'))}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="size-3.5" />
      </button>
      <button
        type="button"
        title="取り消し線"
        className={btn(editor.isActive('strike'))}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="size-3.5" />
      </button>
      <span className="w-px h-4 bg-[var(--border)] mx-1" />
      <button
        type="button"
        title="箇条書き"
        className={btn(editor.isActive('bulletList'))}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="size-3.5" />
      </button>
      <button
        type="button"
        title="番号付きリスト"
        className={btn(editor.isActive('orderedList'))}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="size-3.5" />
      </button>
      <button
        type="button"
        title="引用"
        className={btn(editor.isActive('blockquote'))}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote className="size-3.5" />
      </button>
      <span className="w-px h-4 bg-[var(--border)] mx-1" />
      <button
        type="button"
        title="リンク"
        className={btn(editor.isActive('link'))}
        onClick={setLink}
      >
        <LinkIcon className="size-3.5" />
      </button>
      <span className="flex-1" />
      <button
        type="button"
        title="元に戻す (Cmd+Z)"
        className={btn(false)}
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 className="size-3.5" />
      </button>
      <button
        type="button"
        title="やり直し (Cmd+Shift+Z)"
        className={btn(false)}
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 className="size-3.5" />
      </button>
    </div>
  );
}
